// ===========================================
// "An invoice may need amending" (mid-term start week, D2, 2026-09-30)
// ===========================================
// Shown after a class's first / last week or a student's starting week changes. Lists the sent
// invoices the affected families hold for that term, each with an Amend link (to the invoice
// page, which opens its amend dialog with a banner) or the reason it is locked.
//
// Nothing is auto-amended, and the wording is always "may": an invoice line carries no lesson
// id, so the server cannot tell which invoice billed which class. Keep it that way until a
// column makes it true.

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type { InvoiceLockReason, InvoiceToAmend } from '../../api/invoices.api';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { formatInSchoolTz } from '../../utils/dateUtils';
import { amendChangeSentence, termNamesLabel, type AmendChange } from '../../utils/invoiceAmend';

export type InvoicesToAmendContext = AmendChange & {
  lessonId: string;
  termName: string;
} & ({ kind: 'class' } | { kind: 'student'; studentId: string });

interface InvoicesToAmendDialogProps {
  open: boolean;
  onClose: () => void;
  invoices: InvoiceToAmend[];
  context: InvoicesToAmendContext | null;
}

export const LOCK_TEXT: Record<InvoiceLockReason, string> = {
  PAYMENT_RECORDED: 'Locked — a payment has been recorded; issue a credit or a new invoice',
  PAID: 'Locked — a payment has been recorded; issue a credit or a new invoice',
  CHECKOUT_LIVE: 'The family is paying online right now; open it to see when it can be amended',
  SUBSCRIPTION: "Managed by Stripe; can't be amended here",
};

/** The amend deep link the invoice page reads (and then clears). */
export function amendLinkFor(
  schoolSlug: string | undefined,
  invoiceId: string,
  context: InvoicesToAmendContext
): string {
  const params = new URLSearchParams({ amend: '1', lessonId: context.lessonId });
  if (context.kind === 'student') params.set('studentId', context.studentId);
  return `/${schoolSlug}/admin/invoices/${invoiceId}?${params.toString()}`;
}

/**
 * One row per invoice: number, family, Draft / Sent date, the lock reason, and the Edit (draft) /
 * Amend (sent) / Open (live checkout) link. Shared with AlreadyInvoicedDialog. `linkFor` builds
 * the deep link; following it never clears the host's list (Back brings it back, D-c).
 */
export function InvoiceToAmendRows({
  invoices,
  linkFor,
}: {
  invoices: InvoiceToAmend[];
  linkFor: (invoiceId: string) => string;
}) {
  const timeZone = useSchoolTimezone();
  return (
    <List dense disablePadding>
      {invoices.map((invoice) => {
        const isDraft = invoice.status === 'DRAFT';
        const sentOn = invoice.sentAt
          ? formatInSchoolTz(invoice.sentAt, timeZone, { day: '2-digit', month: '2-digit', year: 'numeric' })
          : null;
        return (
          <ListItem
            key={invoice.id}
            disableGutters
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {invoice.invoiceNumber} &middot; {invoice.familyName}
                {isDraft ? ' · Draft' : sentOn ? ` · Sent ${sentOn}` : ''}
              </Typography>
              {!invoice.amendable && !isDraft && (
                <Typography variant="caption" color="text.secondary">
                  {invoice.lockReason ? LOCK_TEXT[invoice.lockReason] : 'Locked'}
                </Typography>
              )}
            </Box>
            {/* A live checkout is a WAIT, not a lock: the invoice page says until when. */}
            {(isDraft || invoice.amendable || invoice.lockReason === 'CHECKOUT_LIVE') && (
              <Button
                size="small"
                variant="outlined"
                component={RouterLink}
                // No onClose here: leaving the page must NOT clear the list — Back brings
                // it back for the next family (D-c). Only Done clears it.
                to={linkFor(invoice.id)}
              >
                {isDraft ? 'Edit' : invoice.amendable ? 'Amend' : 'Open'}
              </Button>
            )}
          </ListItem>
        );
      })}
    </List>
  );
}

export default function InvoicesToAmendDialog({
  open,
  onClose,
  invoices,
  context,
}: InvoicesToAmendDialogProps) {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  if (!context) return null;

  const intro = context.kind === 'class' ? 'These families were invoiced for' : 'Their family was invoiced for';
  // A draft is EDITED, a sent invoice AMENDED (D-b, 2026-10-01): the title only says
  // "amending" when every row is a sent invoice.
  const hasDraft = invoices.some((invoice) => invoice.status === 'DRAFT');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="invoices-to-amend-title">
      <DialogTitle id="invoices-to-amend-title">
        {hasDraft ? 'An invoice may need updating' : 'An invoice may need amending'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {amendChangeSentence(context)} {intro}{' '}
          {context.kind === 'class' && context.termNames?.length ? termNamesLabel(context.termNames) : context.termName}:
        </Typography>
        <InvoiceToAmendRows invoices={invoices} linkFor={(id) => amendLinkFor(schoolSlug, id, context)} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          We can&apos;t tell which invoices billed this class, so check each one. After each one, your
          browser&apos;s Back button returns you to this list.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
